import React from 'react';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import { Autocomplete, Button, Grid, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { registerUser, updateUser } from '../../actions/user';
import { DIVISIONS } from '../utils/lists';
import { capLetters } from '../../helpers/utils';

// Formik schema
const RegisterSchema = Yup.object().shape({
	first_name: Yup.string()
		.min(2, '¡Demasiado corto!')
		.max(100, '¡Demasiado largo!')
		.nullable(true)
		.required('Se requiere el nombre'),
	last_name: Yup.string()
		.min(2, '¡Demasiado corto!')
		.max(100, '¡Demasiado largo!')
		.nullable(true)
		.required('Se requieren los apellidos'),
	email: Yup.string()
		.min(2, '¡Demasiado corto!')
		.max(50, '¡Demasiado largo!')
		.nullable(true)
		.required('Se requiere un correo electrónico'),
	password: Yup.string()
		// .min(4, '¡Demasiado corto!')
		// .max(50, '¡Demasiado largo!')
		.nullable(true),
	// .required('Se requiere una contraseña'),
	role: Yup.string()
		.min(2, '¡Demasiado corto!')
		.max(50, '¡Demasiado largo!')
		.nullable(true)
		.required('Se requiere un rol'),
	operator: Yup.string()
		.min(2, '¡Demasiado corto!')
		.max(50, '¡Demasiado largo!')
		.nullable(true),
	division: Yup.string()
		.min(2, '¡Demasiado corto!')
		.max(50, '¡Demasiado largo!')
		.nullable(true),
	position: Yup.string()
		.min(2, '¡Demasiado corto!')
		.max(100, '¡Demasiado largo!')
		.nullable(true),
	phone_number: Yup.string()
		.min(2, '¡Demasiado corto!')
		.max(100, '¡Demasiado largo!')
		.nullable(true),
});

export const NewUserForm = ({ handleClose, data = null, refresh = null }) => {
	// Define state to know if it's loading or not
	const [isLoading, setIsLoading] = useState(false);
	const [changePassword, setChangePassword] = useState(false);
	const { area: AREAS, usertype: USER_TYPES } = useSelector(
		(state) => state.catalogs
	);
	const [initialValuesFormik] = useState(
		data
			? {
					first_name: data.first_name,
					last_name: data.last_name,
					email: data.email,
					password: data.password,
					role: data.user_type.name,
					operator: data?.area?.name || null,
					division:
						DIVISIONS.find((item) => item.name === data?.division)
							?.label || null,
					position: data?.position || '',
					phone_number: data?.phone_number || '',
			  }
			: {
					first_name: '',
					last_name: '',
					email: '',
					password: '',
					role: null,
					operator: null,
					user_type: null,
					area: null,
					division: null,
					position: '',
					phone_number: '',
			  }
	);
	const dispatch = useDispatch();

	const formik = useFormik({
		initialValues: initialValuesFormik,
		validationSchema: RegisterSchema,
		onSubmit: () => {
			setIsLoading(true);
			const {
				first_name,
				last_name,
				email,
				password,
				role,
				operator,
				division,
				position,
				phone_number,
			} = formik.values;

			const isAdmin = role.includes('Admin');

			const formData = {
				first_name: capLetters(first_name.trim()),
				last_name: capLetters(last_name.trim()),
				email: email.trim(),
				password,
				is_staff: isAdmin,
				operation_area: isAdmin ? null : operator,
				user_type:
					USER_TYPES.find((item) => item.name === role)?.id || null,
				area: AREAS.find((item) => item.name === operator)?.id || null,
				division: DIVISIONS.find((item) => item?.label === division)
					.name,
				position: capLetters(position),
				phone_number: phone_number,
			};

			handleClose();
			dispatch(
				data !== null
					? updateUser(data, formData)
					: registerUser(formData)
			);
			setTimeout(() => {
				refresh();
				formik.resetForm();
			}, 1000);
			setIsLoading(false);
		},
	});

	const { handleSubmit, getFieldProps, touched, errors } = formik;

	// Text component for reuse
	const TextComp = (name, label, type = 'text') => (
		<TextField
			margin='normal'
			type={type}
			placeholder={name === 'password' ? 'Nueva contraseña' : ''}
			disabled={name === 'password' && data !== null && !changePassword}
			fullWidth
			{...getFieldProps(name)}
			label={label}
			error={Boolean(touched[name] && errors[name])}
			helperText={touched[name] && errors[name]}
		/>
	);

	const SelectComponent = (params, label, name) => (
		<TextField
			required
			margin='normal'
			{...params}
			{...getFieldProps(name)}
			label={label}
			error={Boolean(touched[name] && errors[name])}
			helperText={touched[name] && errors[name]}
		/>
	);

	return (
		<FormikProvider value={formik}>
			<Form autoComplete='off' noValidate onSubmit={handleSubmit}>
				<Grid container spacing={2} className='mt-3'>
					<Grid item xs={6}>
						{/* First name field */}
						{TextComp('first_name', 'Nombre')}
					</Grid>

					<Grid item xs={6}>
						{/* Last name field */}
						{TextComp('last_name', 'Apellidos')}
					</Grid>

					<Grid item xs={6}>
						{/* Email field */}
						{TextComp('email', 'Correo')}
					</Grid>

					{data !== null && (
						<Grid
							item
							xs={6}
							style={
								changePassword
									? {}
									: {
											display: 'flex',
											flexDirection: 'column',
											justifyContent: 'center',
											alignItems: 'center',
											alignContent: 'center',
									  }
							}
						>
							{/* Password field */}
							{changePassword && (
								<>
									{TextComp(
										'password',
										'Contraseña',
										'password'
									)}
								</>
							)}

							<Button
								size='small'
								onClick={() => {
									setChangePassword(!changePassword);
									formik.values.password = '';
								}}
							>
								{!changePassword
									? 'Cambiar contraseña'
									: 'Usar contraseña actual'}
							</Button>
						</Grid>
					)}

					{data === null && (
						<Grid item xs={6}>
							{TextComp('password', 'Contraseña', 'password')}
						</Grid>
					)}

					<Grid item xs={6}>
						<Autocomplete
							required
							// style={{ marginTop: '-17px' }}
							fullWidth
							value={formik.values.division}
							onChange={(_, value) => {
								formik.setFieldValue('division', value);
							}}
							options={DIVISIONS.map((item) => item.label)}
							renderInput={(params) =>
								SelectComponent(params, 'División', 'division')
							}
						/>
					</Grid>

					<Grid item xs={6}>
						{/* Email field */}
						{TextComp('position', 'Puesto')}
					</Grid>

					<Grid item xs={6}>
						{/* Email field */}
						{TextComp('phone_number', 'Número telefónico')}
					</Grid>

					<Grid item xs={6}>
						{/* Role field */}
						<Autocomplete
							required
							// style={{ marginTop: '20px' }}
							fullWidth
							value={formik.values.role}
							onChange={(_, value) => {
								formik.setFieldValue('role', value);
							}}
							options={USER_TYPES.map((item) => item.name)}
							renderInput={(params) =>
								SelectComponent(
									params,
									'Tipo de usuario',
									'role'
								)
							}
						/>
					</Grid>

					{formik.values.role === 'Operador' && (
						<Grid item xs={6}>
							{/* Role field */}
							<Autocomplete
								// style={{ marginTop: '20px' }}
								fullWidth
								value={formik.values.operator}
								onChange={(_, value) => {
									formik.setFieldValue('operator', value);
								}}
								options={AREAS.map((item) => item.name)}
								renderInput={(params) =>
									SelectComponent(params, 'Área', 'operator')
								}
							/>
						</Grid>
					)}

					{/* {data !== null && <Grid item xs={6} />} */}
				</Grid>
				<Grid container spacing={2} className='mt-3'>
					<Grid item xs={6}>
						{/* Cancel button */}
						<LoadingButton
							fullWidth
							size='large'
							color='error'
							onClick={() => {
								formik.resetForm(initialValuesFormik);
								handleClose();
							}}
							variant='contained'
							loading={false}
						>
							Cancelar
						</LoadingButton>
					</Grid>

					<Grid item xs={6}>
						{/* Confirm button */}
						<LoadingButton
							fullWidth
							size='large'
							style={{ backgroundColor: '#143f6c' }}
							type='submit'
							variant='contained'
							loading={isLoading}
						>
							Guardar Usuario
						</LoadingButton>
					</Grid>
				</Grid>
			</Form>
		</FormikProvider>
	);
};
